import { UseAsyncOptions, UseAsyncResponse, useAsync } from '@workday/wcp-cargo-react';
import { Executor, HttpClientResponse } from '../requests/Definitions';
import wconsole from '../util/wconsole';
import { useMountedFlag } from './useMountedFlag';

export type GetOnMountResponse<T> = UseAsyncResponse<T>;

/**
 * @deprecated use cargo's useAsync directly instead. As a team we've decided to deprecate the usage of this hook for new code.
 *
 * This hook is a convenience hook for loading data from a service on a component mount.
 * It is very common that a component needs to render some data from a service in a table or in some other way.
 *
 * Note that the name this service is only get on mount. The naming here is important.
 * The query is only ever executed on mount, and never again. Similar to the useState hook, arguments
 * are only honored here on the first render, so any rerenders that change the passed
 * arguments will be ignored. If more flexibility is needed, we recommend you use the
 * useService hook in conjunction with a useEffect call.
 *
 * We realize there are times when data becomes stale and needs to be updated. To refresh the query,
 * a refresh function is provided as part of the return. This function is stable and pure, and the
 * reference never changes.
 *
 *If this receives a null required parameter, the call will not execute
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
export default function useGetOnMount<T extends any>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  service: Executor<any>,
  query: string,
  onQuerySuccess: (value: HttpClientResponse, query: string) => T | Promise<T> = (res) => res.body,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  onQueryFail?: (err: any) => void,
  options?: UseAsyncOptions
): GetOnMountResponse<T> {
  // useAsync does not cancel in-flight work, so this callback can reject after unmount.
  // Logging that rejection prints after Jest has finished and keeps the worker alive, so only the
  // log is gated. onQueryFail still runs: callers may rely on it after the component is gone.
  const active = useMountedFlag();

  return useAsync<T>(
    async () => {
      try {
        const resp = await service.get(query);
        return onQuerySuccess(resp, query);
      } catch (e) {
        if (active.current) {
          wconsole.error(e);
        }
        onQueryFail?.(e);
        throw e;
      }
    },
    [query],
    { ...(options || {}), ignoreCall: !query || options?.ignoreCall }
  );
}
